import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Bot, Box, Code2, FileArchive, Globe, ImageIcon, Loader2, Lock, Menu, Plus, Star, WifiOff } from "lucide-react";
import VantaAILanding from "@/components/vai/Landing";
import { API, useAuth } from "@/App";
import { tokenStore } from "@/lib/token";
import { errorText, greeting, resetIn, streamMessage, vai } from "@/lib/vai";
import Sidebar from "@/components/vai/Sidebar";
import Composer from "@/components/vai/Composer";
import ArtifactPanel from "@/components/vai/ArtifactPanel";
import SettingsDialog from "@/components/vai/SettingsDialog";
import AppsPage from "@/components/vai/AppsPage";
import UpgradePage from "@/components/vai/UpgradePage";
import ApiKeysPage from "@/components/vai/ApiKeysPage";
import ArtifactsPage from "@/components/vai/ArtifactsPage";
import { ConfirmHost, FeedbackDialog, InstallDialog, confirmDialog } from "@/components/vai/Dialogs";
import { AssistantMessage, UserMessage, VantaMark } from "@/components/vai/Message";
import { collectArtifacts } from "@/components/vai/artifacts";

// Vanta AI (/ai) : l'assistant IA de VantaPulse, propulsé par Vector (un seul
// modèle, entraîné par VantaPulse). Même organisation que claude.ai :
// conversations à gauche, échange au centre, artefacts à droite. Vector et la
// création 3D sont réservés aux offres Pro et Max.

const SUGGESTIONS = [
  { icon: FileArchive, label: "Ressource FiveM", text: "Projet complet en .zip", prompt: "Crée une ressource FiveM complète en .zip (fxmanifest.lua, client, server, config) : " },
  { icon: Globe, label: "Site web", text: "Avec aperçu en direct", prompt: "Crée un site web complet (une page HTML avec aperçu) : " },
  { icon: Bot, label: "Bot Discord", text: "discord.js, prêt à lancer", prompt: "Crée un bot Discord (discord.js v14) complet en .zip : " },
  { icon: Code2, label: "Corriger mon code", text: "Joins tes fichiers avec +", prompt: "Corrige ce code, explique chaque erreur, puis donne la version corrigée : " },
  { icon: ImageIcon, label: "Créer une image", text: "Photo, logo, affiche…", prompt: "Fais-moi une image de ", tool: "image" },
  { icon: Box, label: "Créer en 3D", text: "Aperçu et fichier .glb", prompt: "Crée un modèle 3D de ", tool: "model3d" },
];

const readPref = (key, fallback) => { try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch { return fallback; } };
const writePref = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* stockage indisponible */ } };

function VantaAIApp() {
  const { chatId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const upgradeView = location.pathname.startsWith("/ai/upgrade");
  const apiView = location.pathname.startsWith("/ai/api");
  const artifactsView = location.pathname.startsWith("/ai/artifacts");
  const appsView = location.pathname.startsWith("/ai/apps");
  const { logout } = useAuth();

  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [chats, setChats] = useState([]);
  const [folders, setFolders] = useState([]);
  const [chat, setChat] = useState(null); // { id, title, model, starred, messages }
  const [chatLoading, setChatLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [model, setModel] = useState("pulse");
  const [attachments, setAttachments] = useState([]);
  const [effort, setEffort] = useState(() => readPref("vai_effort", "moyen"));
  const [tools, setTools] = useState([]);
  const [toolMode, setToolModeState] = useState(() => (readPref("vai-tool-mode", "auto") === "manual" ? "manual" : "auto"));
  const setToolMode = (mode) => { setToolModeState(mode); writePref("vai-tool-mode", mode); if (mode === "auto") setTools([]); };
  const [streaming, setStreaming] = useState(null); // { message, runningTool }
  const [artifactId, setArtifactId] = useState(null);
  const [wide, setWide] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readPref("vai_sidebar_collapsed", false));
  const [mobileNav, setMobileNav] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dialog, setDialog] = useState(null); // install | feedback
  const installPrompt = useRef(null);
  const [installed, setInstalled] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);
  const autoOpened = useRef(new Set());

  const refreshMe = useCallback(() => vai.get("/me").then((m) => { setMe(m); return m; }), []);
  const refreshChats = useCallback(() => Promise.all([
    vai.get("/chats").then((r) => setChats(r.chats)),
    vai.get("/folders").then((r) => setFolders(r.folders)),
  ]).catch(() => {}), []);

  useEffect(() => {
    refreshMe().then((m) => {
      const allowed = m.models.filter((x) => x.allowed);
      setModel((allowed.find((x) => x.id === m.settings.default_model) || allowed[allowed.length - 1] || m.models[0])?.id || "pulse");
    })
      .catch((err) => setLoadError(errorText(err, "Vanta AI ne répond pas")));
    refreshChats();
  }, [refreshMe, refreshChats]);

  // /ai?q=… (plugin JetBrains, liens) : la question est préremplie, prête à envoyer.
  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q");
    if (!q) return;
    setDraft(q.slice(0, 20000));
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Retour du paiement Stripe.
  useEffect(() => {
    const sid = new URLSearchParams(location.search).get("upgraded");
    if (!sid) return;
    navigate(location.pathname, { replace: true });
    axios.get(`${API}/checkout/status/${sid}`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
      .then((r) => { if (r.data.payment_status === "paid" || r.data.status === "complete") toast.success("Abonnement activé, merci !"); else toast.info("Paiement en cours de validation"); })
      .catch(() => toast.error("Paiement non confirmé"))
      .finally(() => refreshMe().catch(() => {}));
  }, [location.search, location.pathname, navigate, refreshMe]);

  // Conversation ouverte.
  useEffect(() => {
    setArtifactId(null);
    if (!chatId) { setChat(null); return; }
    if (chat && chat.id === chatId) return;
    setChatLoading(true);
    vai.get(`/chats/${chatId}`).then((c) => { setChat(c); stickRef.current = true; })
      .catch(() => { toast.error("Conversation introuvable"); navigate("/ai", { replace: true }); })
      .finally(() => setChatLoading(false));
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Réponse encore en cours (page rechargée pendant une création 3D, par ex.) : on attend
  // le résultat et il s'affiche dès qu'il est prêt, sans avoir à recharger.
  const pollRef = useRef(null);
  useEffect(() => {
    if (streaming) return undefined;
    const msgs = chat?.messages || [];
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== "assistant" || !last.pending || pollRef.current === last.id) return undefined;
    pollRef.current = last.id;
    let stopped = false;
    (async () => {
      for (let i = 0; i < 72 && !stopped; i += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        let fresh;
        try { fresh = await vai.get(`/chats/${chat.id}`); } catch { continue; }
        const got = fresh.messages.find((m) => m.id === last.id);
        if (!got || !got.pending) { setChat((c) => (c && c.id === fresh.id ? fresh : c)); break; }
      }
      if (pollRef.current === last.id) pollRef.current = null;
    })();
    return () => { stopped = true; };
  }, [chat, streaming]); // eslint-disable-line react-hooks/exhaustive-deps

  // Une conversation rouverte reprend avec son modèle, s'il est inclus dans l'offre.
  useEffect(() => {
    if (chat?.model && me?.models.find((x) => x.id === chat.model && x.allowed)) setModel(chat.model);
  }, [chat?.id, me?.plan?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { writePref("vai_sidebar_collapsed", collapsed); }, [collapsed]);
  useEffect(() => { writePref("vai_effort", effort); }, [effort]);

  // Ctrl+Maj+, (⇧⌘, sur Mac) : Réglages, comme sur claude.ai.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "," || e.code === "Comma")) { e.preventDefault(); setSettings("general"); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Application installable : manifeste propre à Vanta AI, invitation du navigateur gardée pour le menu.
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/vanta-ai.webmanifest";
    document.head.appendChild(link);
    // iPhone (« Sur l'écran d'accueil ») : icône bleue de Vanta AI au lieu de celle du site.
    const touch = document.querySelector("link[rel='apple-touch-icon']");
    const siteTouch = touch?.getAttribute("href");
    if (touch) touch.setAttribute("href", "/vanta-ai-apple-180.png");
    const keep = (e) => { e.preventDefault(); installPrompt.current = e; };
    const done = () => { setInstalled(true); installPrompt.current = null; };
    window.addEventListener("beforeinstallprompt", keep);
    window.addEventListener("appinstalled", done);
    if (window.matchMedia?.("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      link.remove();
      if (touch && siteTouch) touch.setAttribute("href", siteTouch);
      window.removeEventListener("beforeinstallprompt", keep);
      window.removeEventListener("appinstalled", done);
    };
  }, []);

  const messages = useMemo(() => {
    const list = chat?.messages || [];
    return streaming ? [...list, streaming.message] : list;
  }, [chat, streaming]);
  const artifacts = useMemo(() => collectArtifacts(messages), [messages]);

  // Ouvert depuis la page Artefacts (?artifact=id) : le panneau s'ouvre sur cet artefact.
  useEffect(() => {
    const want = new URLSearchParams(location.search).get("artifact");
    if (!want || !chat || chat.id !== chatId) return;
    if (artifacts.has(want)) setArtifactId(want);
    navigate(location.pathname, { replace: true });
  }, [chat, artifacts, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Un artefact commence à s'écrire : le panneau s'ouvre (une fois par artefact).
  useEffect(() => {
    if (!streaming) return;
    for (const [id] of artifacts) {
      const owner = artifacts.get(id).slice(-1)[0];
      if (owner.messageId === streaming.message.id && !autoOpened.current.has(id)) {
        autoOpened.current.add(id);
        setArtifactId(id);
      }
    }
  }, [artifacts, streaming]);

  // Défilement : on suit la réponse tant que l'utilisateur est en bas.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);
  const onScroll = () => {
    const el = scrollRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // keepFiles : pièces jointes du message réessayé ou modifié (déjà envoyées au site).
  const send = async (text, base = chat, keepFiles = null) => {
    const files = keepFiles || (text === undefined ? attachments.filter((a) => a.status === "ready") : []);
    const content = (text ?? draft).trim();
    if ((!content && !files.length) || streaming) return;
    let current = base;
    try {
      if (!current) {
        const created = await vai.post("/chats", { model });
        current = { ...created, messages: [] };
        setChats((cs) => [created, ...cs]);
        setChat(current);
        navigate(`/ai/chat/${created.id}`, { replace: !chatId });
      }
    } catch (err) { toast.error(errorText(err)); return; }

    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: content || "Voici mes fichiers.", created_at: new Date().toISOString(),
      attachments: files.map((a) => ({ id: a.id, name: a.name, kind: a.kind, note: a.note })) };
    const assistant = { id: `tmp-a-${Date.now()}`, role: "assistant", model, blocks: [] };
    setChat({ ...current, messages: [...current.messages, tempUser] });
    setDraft("");
    if (files.length && !keepFiles) setAttachments([]);
    stickRef.current = true;
    let live = { message: assistant, runningTool: null };
    setStreaming(live);
    const update = (fn) => { live = fn(live); setStreaming(live); };
    const controller = new AbortController();
    abortRef.current = controller;
    let finalMessage = null;
    try {
      await streamMessage(current.id, { content, model, tools: toolMode === "manual" ? tools : [], tool_mode: toolMode, attachments: files.map((a) => a.id), effort: model === "vector" ? effort : undefined }, (name, data) => {
        if (name === "start") {
          setChat((c) => (c ? { ...c, title: data.title || c.title, messages: c.messages.map((m) => (m.id === tempUser.id ? data.user_message : m)) } : c));
          setChats((cs) => cs.map((x) => (x.id === current.id ? { ...x, title: data.title || x.title, updated_at: new Date().toISOString() } : x)));
          update((l) => ({ ...l, message: { ...l.message, id: data.message_id } }));
        } else if (name === "thinking") {
          update((l) => {
            const blocks = [...l.message.blocks];
            const first = blocks[0];
            if (first && first.type === "thinking") blocks[0] = { ...first, text: first.text + data.text };
            else blocks.unshift({ type: "thinking", text: data.text });
            return { ...l, message: { ...l.message, blocks } };
          });
        } else if (name === "text") {
          update((l) => {
            const blocks = [...l.message.blocks];
            const last = blocks[blocks.length - 1];
            if (last && last.type === "text") blocks[blocks.length - 1] = { ...last, text: last.text + data.text };
            else blocks.push({ type: "text", text: data.text });
            return { ...l, message: { ...l.message, blocks } };
          });
        } else if (name === "tool_start") {
          update((l) => ({ ...l, runningTool: data }));
        } else if (name === "tool") {
          update((l) => ({ runningTool: null, message: { ...l.message, blocks: [...l.message.blocks, data] } }));
        } else if (name === "error") {
          update((l) => ({ ...l, message: { ...l.message, error: data.detail } }));
        } else if (name === "title") {
          // Titre résumé par l'IA (remplace le début du premier message).
          setChat((c) => (c && c.id === current.id ? { ...c, title: data.title } : c));
          setChats((cs) => cs.map((x) => (x.id === current.id ? { ...x, title: data.title } : x)));
        } else if (name === "done") {
          finalMessage = data.message;
          setMe((m) => (m ? { ...m, usage: data.usage } : m));
        }
      }, controller.signal);
    } catch (err) {
      if (err.name === "AbortError") {
        finalMessage = { ...live.message, stopped: true };
      } else {
        // Refusé avant la réponse (offre, limite, moteur indisponible) : rien n'a été enregistré.
        toast.error(err.message);
        setChat((c) => (c ? { ...c, messages: c.messages.filter((m) => m.id !== tempUser.id) } : c));
        setDraft(content);
        if (files.length && !keepFiles) setAttachments(files);
        setStreaming(null);
        abortRef.current = null;
        if (err.status === 429 || err.status === 403) refreshMe().catch(() => {});
        return;
      }
    }
    abortRef.current = null;
    // Connexion coupée avant la fin (proxy, réseau) : la création continue sur le serveur ; la
    // page vérifie la conversation toutes les 5 secondes et affiche le résultat dès qu'il existe.
    const lost = !finalMessage && !live.message.stopped && !String(live.message.id).startsWith("tmp-");
    const done = finalMessage || (lost ? { ...live.message, pending: true } : live.message);
    setChat((c) => (c ? { ...c, messages: [...c.messages, done] } : c));
    setStreaming(null);
    if (lost) {
      for (let i = 0; i < 72; i += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        let fresh;
        try { fresh = await vai.get(`/chats/${current.id}`); } catch { continue; }
        const got = fresh.messages.find((m) => m.id === done.id);
        if (got && !got.pending) {
          setChat((c) => (c && c.id === current.id ? fresh : c));
          break;
        }
      }
    }
    // Artefact écrit dans cette réponse et pas encore ouvert (réponse très rapide) : on l'ouvre.
    const fresh = [...collectArtifacts([done]).keys()].filter((id) => !autoOpened.current.has(id));
    if (fresh.length) { fresh.forEach((id) => autoOpened.current.add(id)); setArtifactId(fresh[fresh.length - 1]); }
  };

  const stop = () => abortRef.current?.abort();

  const retry = async () => {
    const list = chat?.messages || [];
    const lastUser = [...list].reverse().find((m) => m.role === "user");
    if (!lastUser || streaming) return;
    try {
      await vai.post(`/chats/${chat.id}/truncate`, { from_message_id: lastUser.id });
      const kept = list.slice(0, list.indexOf(lastUser));
      const next = { ...chat, messages: kept };
      setChat(next);
      send(lastUser.content, next, lastUser.attachments || null);
    } catch (err) { toast.error(errorText(err)); }
  };

  const editMessage = async (message, content) => {
    if (streaming) return;
    try {
      await vai.post(`/chats/${chat.id}/truncate`, { from_message_id: message.id });
      const list = chat.messages;
      const next = { ...chat, messages: list.slice(0, list.indexOf(message)) };
      setChat(next);
      send(content, next, message.attachments || null);
    } catch (err) { toast.error(errorText(err)); }
  };

  const rateMessage = async (message, rating, note = "") => {
    const setRating = (value) => setChat((c) => (c ? { ...c, messages: c.messages.map((m) => (m.id === message.id ? { ...m, rating: value } : m)) } : c));
    const before = message.rating;
    setRating(rating || undefined);
    try {
      await vai.post(`/messages/${message.id}/rating`, { rating, note });
      if (rating) toast.success("Merci ! Ton avis aide à améliorer Vector.");
    } catch (err) { setRating(before); toast.error(errorText(err)); }
  };

  const newChat = () => { if (streaming) stop(); setDraft(""); setTools([]); navigate("/ai"); };
  const openChat = (id) => { if (streaming) stop(); navigate(`/ai/chat/${id}`); };
  const renameChat = async (c, title) => {
    try { const r = await vai.patch(`/chats/${c.id}`, { title }); setChats((cs) => cs.map((x) => (x.id === c.id ? { ...x, ...r } : x))); if (chat?.id === c.id) setChat((ch) => ({ ...ch, title: r.title })); }
    catch (err) { toast.error(errorText(err)); }
  };
  const starChat = async (c) => {
    try { const r = await vai.patch(`/chats/${c.id}`, { starred: !c.starred }); setChats((cs) => cs.map((x) => (x.id === c.id ? { ...x, ...r } : x))); if (chat?.id === c.id) setChat((ch) => ({ ...ch, starred: r.starred })); }
    catch (err) { toast.error(errorText(err)); }
  };
  const copyLink = async (c) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/ai/chat/${c.id}`); toast.success("Lien copié (il ne s'ouvre que sur ton compte)"); }
    catch { toast.error("Copie impossible"); }
  };
  const moveChat = async (c, folderId) => {
    try {
      const r = await vai.patch(`/chats/${c.id}`, { folder_id: folderId });
      setChats((cs) => cs.map((x) => (x.id === c.id ? { ...x, ...r } : x)));
      toast.success(folderId ? `Déplacée dans « ${folders.find((f) => f.id === folderId)?.name || "le dossier"} »` : "Retirée du dossier");
    } catch (err) { toast.error(errorText(err)); }
  };
  const createFolder = async (name) => {
    try { const f = await vai.post("/folders", { name }); setFolders((fs) => [...fs, f]); return f; }
    catch (err) { toast.error(errorText(err)); return null; }
  };
  const renameFolder = async (f, name) => {
    try { await vai.patch(`/folders/${f.id}`, { name }); setFolders((fs) => fs.map((x) => (x.id === f.id ? { ...x, name } : x))); }
    catch (err) { toast.error(errorText(err)); }
  };
  const deleteFolder = async (f) => {
    if (!await confirmDialog({ title: `Supprimer le dossier « ${f.name} » ?`, text: "Ses conversations ne sont pas supprimées : elles reviennent dans Récents.", confirmLabel: "Supprimer le dossier", danger: true })) return;
    try {
      await vai.del(`/folders/${f.id}`);
      setFolders((fs) => fs.filter((x) => x.id !== f.id));
      setChats((cs) => cs.map((x) => (x.folder_id === f.id ? { ...x, folder_id: "" } : x)));
    } catch (err) { toast.error(errorText(err)); }
  };
  const deleteChat = async (c) => {
    if (!await confirmDialog({ title: "Supprimer la conversation ?", text: `« ${c.title} » sera supprimée définitivement.`, confirmLabel: "Supprimer", danger: true })) return;
    try {
      await vai.del(`/chats/${c.id}`);
      // Elle glisse hors de la liste avant de disparaître.
      setChats((cs) => cs.map((x) => (x.id === c.id ? { ...x, leaving: true } : x)));
      setTimeout(() => setChats((cs) => cs.filter((x) => x.id !== c.id)), 200);
      if (chat?.id === c.id) navigate("/ai");
      toast.success("Conversation supprimée");
    }
    catch (err) { toast.error(errorText(err)); }
  };
  const upgrade = () => { setSettings(null); navigate("/ai/upgrade"); };
  const setLanguage = async (language) => {
    try { const s = await vai.put("/settings", { language }); setMe((m) => ({ ...m, settings: s })); toast.success("Langue des réponses enregistrée"); }
    catch (err) { toast.error(errorText(err)); }
  };
  const install = async () => {
    const prompt = installPrompt.current;
    if (prompt) {
      installPrompt.current = null;
      prompt.prompt();
      const choice = await prompt.userChoice.catch(() => null);
      if (choice?.outcome === "accepted") { setInstalled(true); toast.success("Vanta AI est installé"); }
      return;
    }
    setDialog("install");
  };
  const signOut = async () => { await logout(); navigate("/"); };
  const account = {
    onSettings: (section) => setSettings(section), onUpgrade: upgrade, onLanguage: setLanguage,
    onInstall: () => navigate("/ai/apps"), onFeedback: () => setDialog("feedback"), onLogout: signOut,
  };

  if (loadError) {
    return (
      <div className="relative z-[2] h-screen flex items-center justify-center bg-[#0e0e14] px-4 text-center">
        <div><AlertTriangle className="w-8 h-8 text-amber-300 mx-auto" /><p className="mt-3">{loadError}</p><button type="button" className="mt-4 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm" onClick={() => window.location.reload()}>Réessayer</button></div>
      </div>
    );
  }
  if (!me) return <div className="relative z-[2] h-screen flex items-center justify-center bg-[#0e0e14]"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>;

  const modelCost = model === "vector" ? (me.efforts || []).find((e) => e.id === effort)?.cost || 2 : me.models.find((m) => m.id === model)?.cost || 1;
  const remaining = Math.min(me.usage.session.limit - me.usage.session.used, me.usage.weekly.limit - me.usage.weekly.used);
  const limited = remaining < modelCost;
  const sessionPct = me.usage.session.used / Math.max(1, me.usage.session.limit);
  const weeklyPct = me.usage.weekly.used / Math.max(1, me.usage.weekly.limit);
  const nearLimit = !limited && Math.max(sessionPct, weeklyPct) >= 0.8;
  const resets = sessionPct >= weeklyPct ? me.usage.session.resets_at : me.usage.weekly.resets_at;
  const modelInfo = me.models.find((m) => m.id === model);
  const offline = !me.engine.online || (modelInfo && !modelInfo.available);
  const disabledReason = limited ? `Limite atteinte : elle se recharge ${resetIn(resets)}` : offline ? "Vanta AI est momentanément indisponible" : "";
  const artifactVersions = artifactId ? artifacts.get(artifactId) : null;
  const lastAssistant = [...(chat?.messages || [])].reverse().find((m) => m.role === "assistant");

  const composer = (
    <Composer value={draft} onChange={setDraft} onSend={() => send()} onStop={stop} streaming={!!streaming} disabled={limited || offline} disabledReason={disabledReason}
      me={me} model={model} onModel={setModel} effort={effort} onEffort={setEffort} tools={tools} onTools={setTools} toolMode={toolMode} onToolMode={setToolMode} attachments={attachments} onAttachments={setAttachments}
      onArtifacts={() => navigate("/ai/artifacts")} onUpgrade={upgrade} autoFocus={!chatId}
      placeholder={chatId ? `Répondre à ${me.models.find((m) => m.id === model)?.name || "Vanta AI"}…` : "Comment puis-je t'aider ?"} />
  );
  const notices = (
    <>
      {offline && <div className="mb-2 flex items-center gap-2 rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="vai-offline"><WifiOff className="w-4 h-4 shrink-0" />{me.engine.online ? "Vector redémarre (nouvelle version en cours d'installation) : réessaie dans quelques minutes." : "Vanta AI démarre ou est en maintenance : réessaie dans quelques minutes."}</div>}
      {limited && <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-xs text-red-100" data-testid="vai-limit"><AlertTriangle className="w-4 h-4 shrink-0" />Tu as atteint ta limite d'utilisation. Elle se recharge {resetIn(resets)}.{me.plan.id !== "max" && <button type="button" onClick={upgrade} className="underline ml-auto">Passer à {me.plan.id === "free" ? "Pro" : "Max"}</button>}</div>}
      {nearLimit && <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/70"><AlertTriangle className="w-4 h-4 shrink-0 text-amber-300" />Il te reste peu d'utilisation (recharge {resetIn(resets)}).{me.plan.id !== "max" && <button type="button" onClick={upgrade} className="underline ml-auto">Plus d'utilisation</button>}</div>}
    </>
  );

  return (
    <div className="relative z-[2] h-[100dvh] flex bg-[#0e0e14] text-white overflow-hidden" data-testid="vai-page">
      <Sidebar me={me} chats={chats} currentId={chatId} collapsed={collapsed} onCollapse={setCollapsed} mobileOpen={mobileNav} onMobileClose={() => setMobileNav(false)}
        onNew={newChat} onOpen={openChat} onRename={renameChat} onStar={starChat} onDelete={deleteChat} account={account}
        folders={folders} onCopyLink={copyLink} onMove={moveChat} onCreateFolder={createFolder} onRenameFolder={renameFolder} onDeleteFolder={deleteFolder}
        onArtifacts={() => { if (streaming) stop(); navigate("/ai/artifacts"); }} artifactsActive={artifactsView} />

      <main className="flex-1 min-w-0 flex">
        <div className={`flex-1 min-w-0 flex flex-col ${artifactVersions ? `hidden md:flex ${wide ? "md:max-w-[35%]" : "md:max-w-[50%]"}` : ""}`}>
          <header className="h-14 shrink-0 flex items-center gap-2 px-3 lg:px-4 border-b border-white/[0.05]">
            <button type="button" onClick={() => setMobileNav(true)} className="lg:hidden p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5" aria-label="Menu"><Menu className="w-5 h-5" /></button>
            {upgradeView || apiView || artifactsView || appsView ? <span className="text-sm text-white/60">{apiView ? "Clés API" : artifactsView ? "Artefacts" : appsView ? "Applications" : "Offres"}</span> : chat ? (
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-sm font-medium truncate" data-testid="vai-chat-title">{chat.title}</span>
                <button type="button" onClick={() => starChat(chat)} className="p-1 rounded-md text-white/40 hover:text-white" aria-label={chat.starred ? "Retirer des favoris" : "Ajouter aux favoris"}><Star className={`w-4 h-4 ${chat.starred ? "fill-amber-300 text-amber-300" : ""}`} /></button>
              </div>
            ) : <span className="text-sm text-white/60 lg:hidden">Vanta AI</span>}
            <div className="ml-auto flex items-center gap-2">
              {me.plan.id === "free" && !upgradeView && !apiView && !artifactsView && !appsView && <button type="button" onClick={upgrade} className="text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/80 hover:bg-white/5" data-testid="vai-upgrade-button">Passer à Pro</button>}
              <button type="button" onClick={newChat} className="lg:hidden p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5" aria-label="Nouvelle conversation"><Plus className="w-5 h-5" /></button>
            </div>
          </header>

          <div key={upgradeView ? "upgrade" : apiView ? "api" : artifactsView ? "artifacts" : appsView ? "apps" : chatId || "home"} className="vai-rise flex-1 min-h-0 flex flex-col">
          {upgradeView ? <UpgradePage me={me} onBack={() => navigate(-1)} /> : appsView ? <AppsPage me={me} installed={installed} onInstall={install} onUpgrade={upgrade} onBack={() => navigate("/ai")} /> : apiView ? <ApiKeysPage me={me} onBack={() => navigate("/ai")} onUpgrade={upgrade} />
            : artifactsView ? <ArtifactsPage onBack={() => navigate("/ai")} onOpen={(a) => navigate(`/ai/chat/${a.chat_id}?artifact=${encodeURIComponent(a.id)}`)} /> : !chatId ? (
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div className="flex-1 flex flex-col justify-center w-full max-w-2xl mx-auto px-4 py-10">
                <div className="text-center mb-8">
                  <div className="relative w-14 h-14 mx-auto">
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500/40 to-violet-600/40 blur-xl" aria-hidden="true" />
                    <VantaMark className="relative w-14 h-14" />
                  </div>
                  <h1 className="mt-5 text-3xl sm:text-4xl font-medium tracking-tight" data-testid="vai-greeting">{greeting(me.settings.name || me.user.username)}</h1>
                  <p className="mt-2 text-sm sm:text-base text-white/50">
                    {model === "vector" ? <>Tu parles à <span className="text-white/85 font-medium">Vector · Vanta Code</span> : projets complets, corrections, 3D.</>
                      : <>Tu parles à <span className="text-white/85 font-medium">Pulse</span>, rapide pour le quotidien.{!me.models.find((m) => m.id === "vector")?.allowed && <> <button type="button" onClick={upgrade} className="underline decoration-white/30 hover:text-white">Vector · Vanta Code</button> pour les gros projets.</>}</>}
                  </p>
                </div>
                {notices}
                {composer}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4" data-testid="vai-suggestions">
                  {SUGGESTIONS.map((s) => {
                    const toolInfo = s.tool && me.tools.find((t) => t.id === s.tool);
                    const locked = !!s.tool && !toolInfo?.allowed;
                    return (
                      <button key={s.label} type="button" data-testid={`vai-suggestion-${s.tool || s.label}`}
                        onClick={() => { if (locked) { upgrade(); return; } setDraft(s.prompt); if (s.tool && toolMode === "manual") setTools([s.tool]); }}
                        className="group relative text-left rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5 hover:bg-white/[0.05] hover:border-white/15 transition-colors">
                        <span className="flex items-center gap-2 text-sm text-white/80 group-hover:text-white">
                          <s.icon className={`w-4 h-4 shrink-0 ${locked ? "text-white/40" : "text-sky-300"}`} />
                          <span className="truncate">{s.label}</span>
                          {locked && <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-md border border-violet-400/30 bg-violet-500/10 text-violet-200"><Lock className="w-2.5 h-2.5" />Pro</span>}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-white/40 truncate">{s.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto">
                <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
                  {chatLoading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>}
                  {messages.map((m) => (
                    <div key={m.id} className="vai-msg">
                      {m.role === "user"
                        ? <UserMessage message={m} onEdit={editMessage} disabled={!!streaming || m.id.startsWith("tmp-")} />
                        : <AssistantMessage message={m} streaming={streaming?.message === m} runningTool={streaming?.message === m ? streaming.runningTool : null}
                            onOpenArtifact={setArtifactId} activeArtifact={artifactId} onRetry={!streaming ? retry : null} onRate={rateMessage} isLast={m === lastAssistant} />}
                    </div>
                  ))}
                </div>
              </div>
              <div className="shrink-0 px-4 pb-3 pt-2">
                <div className="max-w-3xl mx-auto">
                  {notices}
                  {composer}
                  <p className="text-center text-[11px] text-white/30 mt-2">Vanta AI peut se tromper. Vérifie les informations importantes.</p>
                </div>
              </div>
            </>
          )}
          </div>
        </div>

        {artifactVersions && (
          <div className="vai-panel-in fixed inset-0 z-40 md:static md:z-auto md:flex-1 md:min-w-0">
            <ArtifactPanel key={artifactId} versions={artifactVersions} onClose={() => setArtifactId(null)} wide={wide} onToggleWide={() => setWide(!wide)} />
          </div>
        )}
      </main>

      {settings && (
        <SettingsDialog me={me} initial={settings} onClose={() => setSettings(null)} onChanged={setMe} onUpgrade={upgrade}
          onDeletedAll={() => { setChats([]); setSettings(null); navigate("/ai"); }} />
      )}
      {dialog === "install" && <InstallDialog installed={installed} onClose={() => setDialog(null)} />}
      {dialog === "feedback" && <FeedbackDialog onClose={() => setDialog(null)} />}
      <ConfirmHost />
    </div>
  );
}

export default function VantaAIPage() {
  const { user, loading, passwordVerified } = useAuth();
  // Onglet du navigateur : le logo bleu de l'IA (celui du site revient en quittant /ai).
  // Le titre « VantaPulse – AI » vient de lib/seo.js.
  useEffect(() => {
    const icon = document.querySelector("link[rel~='icon']");
    const siteIcon = icon?.getAttribute("href");
    if (icon) icon.setAttribute("href", "/vanta-ai-tab.png");
    return () => { if (icon && siteIcon) icon.setAttribute("href", siteIcon); };
  }, []);
  if (loading) return <div className="relative z-[2] h-screen flex items-center justify-center bg-[#0e0e14]"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>;
  if (!user) return <VantaAILanding />;
  if (!passwordVerified) return <Navigate to="/password-login" replace />;
  return <VantaAIApp />;
}
